import { AbpMultiTenancyService } from 'abp-ng2-module';
import { Injectable } from '@angular/core';
import { ApplicationInfoDto, GetCurrentLoginInformationsOutput, SessionServiceProxy, TenantLoginInfoDto, UserLoginInfoDto, TraceTenantRegistrationOutput, CheckinUserOutput } from '@shared/service-proxies/service-proxies';
import { ActivatedRoute, Router } from '@angular/router';
import { DataService } from '@app/shared/common/auth/data.service';
import {
    holdsOnboardingDeferral,
    keepsCompletionMark,
    OnboardingDeferral,
    onboardingDeferralFor,
} from '@app/shared/common/auth/onboarding-routing-policy';
import { AppConsts } from '@shared/commonHelper/AppConsts';

@Injectable()
export class AppSessionService {

    private _user: UserLoginInfoDto;
    private _tenant: TenantLoginInfoDto;
    private _application: ApplicationInfoDto;
    private _regStatus: TraceTenantRegistrationOutput;
    private _userCheckin: CheckinUserOutput;
    // Whether getCurrentUserInfoSubject already refreshes this session (init() runs from many places; wire it once)
    private _currentUserInfoRefreshWired = false;
    // D-08 deferral contract: the in-memory latch of the onboarding store-load-failure exit (never persisted, never
    // completion; lost on the next full load or sign-in)
    private _onboardingDeferral: OnboardingDeferral | null = null;

    constructor(
        private _router: Router,
        private _sessionService: SessionServiceProxy,
        private _dataService: DataService,
        private _abpMultiTenancyService: AbpMultiTenancyService,
        private activatedRoute: ActivatedRoute) {
    }

    get application(): ApplicationInfoDto {
        return this._application;
    }

    get user(): UserLoginInfoDto {
        return this._user;
    }

    get userCheckin(): CheckinUserOutput {
        return this._userCheckin;
    }

    get userId(): number {
        return this.user ? this.user.id : null;
    }

    get tenant(): TenantLoginInfoDto {
        return this._tenant;
    }

    get regStatus(): TraceTenantRegistrationOutput {
        return this._regStatus;
    }

    get tenancyName(): string {
        return this._tenant ? this.tenant.tenancyName : '';
    }

    get tenantId(): number {
        return this.tenant ? this.tenant.id : null;
    }

    getShownLoginName(): string {
        const userName = this._user.userName;
        if (!this._abpMultiTenancyService.isEnabled) {
            return userName;
        }

        return (this._tenant ? this._tenant.tenancyName : '.') + '\\' + userName;
    }

    init(): Promise<boolean> {
        return new Promise<boolean>((resolve, reject) => {
            this.getLoginInfo(resolve, reject);
        });
    }

    private getLoginInfo(resolve: (value?: boolean | PromiseLike<boolean>) => void, reject: (reason?: any) => void) {
        this._sessionService.getCurrentLoginInformations().toPromise().then((result: GetCurrentLoginInformationsOutput) => {
            this._application = result.application;
            this._user = result.user;
            this.setTenant(result.tenant);
            this._userCheckin = result.checkinUser;
            if (result.tenant) {
                window.localStorage.setItem('isWorkSummaryRequired', JSON.stringify(result.user ? result.user.isWorkSummaryRequired : false));
            }
            if (this._userCheckin !== undefined && this._user != null) {
                if (location.href.includes('consent-start')) {
                    this._router.navigate(['/welcome/consent-start']);
                } else if (location.href.includes('consent-end')) {
                    let params = (new URL(location.href)).searchParams;
                    const code_parameter = params.get('code');
                    AppConsts.teamAuthCode = code_parameter;
                    this._router.navigate(['/welcome/consent-end']);
                }
            }
            if (!this._currentUserInfoRefreshWired) {
                this._currentUserInfoRefreshWired = true;
                this._dataService.getCurrentUserInfoSubject.subscribe(() => {
                    this.getCurrentUserInfo();
                });
            }

            resolve(true);
        }, (err) => {
            reject(err);
        });
    }

    getCurrentUserInfo(): void {
        this._sessionService.getCurrentLoginInformations().subscribe(result => {
            this._application = result.application;
            this._user = result.user;
            this.setTenant(result.tenant);
            this._userCheckin = result.checkinUser;
            if (result.tenant) {
                window.localStorage.setItem('isWorkSummaryRequired', JSON.stringify(result.user ? result.user.isWorkSummaryRequired : false));
            }
        });
    }

    /**
     * D-08 completion contract (preferred form): marks the in-memory workspace as started after the server's Complete
     * or FallbackComplete answered with success (then navigate with replaceUrl; see onboarding-routing-policy.ts).
     */
    markGetStartedCompleted(): void {
        if (this._tenant) {
            this._tenant.isGetStartedCompleted = true;
        }
    }

    /**
     * D-08 deferral contract (the exit when the onboarding store fails to load, or after a completion call failed):
     * defers onboarding for the signed-in user and workspace in this app instance only, so the route guards stop
     * sending the owner to onboarding (then navigate with replaceUrl; see onboarding-routing-policy.ts). Writes
     * nothing to the server, to browser storage or to isGetStartedCompleted, and never marks completion. Returns
     * whether a latch was set (false without a signed-in workspace user).
     */
    deferOnboarding(): boolean {
        this._onboardingDeferral = onboardingDeferralFor(this._user, this._tenant);
        return this._onboardingDeferral !== null;
    }

    /** D-08: whether the signed-in user deferred onboarding in this app instance (`onboardingDeferred` route fact). */
    get onboardingDeferred(): boolean {
        return holdsOnboardingDeferral(this._onboardingDeferral, this._user, this._tenant);
    }

    /** D-08: a reload never clears a completion mark made in this app instance (the server never sets it to false). */
    private setTenant(next: TenantLoginInfoDto): void {
        const previous: TenantLoginInfoDto = this._tenant;

        this._tenant = next;
        if (keepsCompletionMark(next, previous)) {
            next.isGetStartedCompleted = true;
        }
    }

    changeTenantIfNeeded(tenantId?: number): boolean {
        if (this.isCurrentTenant(tenantId)) {
            return false;
        }

        abp.multiTenancy.setTenantIdCookie(tenantId);
        return true;
    }

    private isCurrentTenant(tenantId?: number) {
        if (!tenantId && this.tenant) {
            return false;
        } else if (tenantId && (!this.tenant || this.tenant.id !== tenantId)) {
            return false;
        }

        return true;
    }
}
